import qrcode from "qrcode-generator";
import { useMemo } from "react";

interface Props {
	value: string;
	/** Rendered size in CSS pixels. */
	size?: number;
	label?: string;
}

/**
 * Drawn as SVG rather than a canvas so it stays sharp on any screen and can be
 * screenshotted or printed for the fridge.
 */
export function QrCode({ value, size = 200, label }: Props) {
	const { path, count } = useMemo(() => {
		// Type 0 lets the encoder pick the smallest version that fits; medium
		// error correction survives a smeared tablet screen.
		const qr = qrcode(0, "M");
		qr.addData(value);
		qr.make();

		const modules = qr.getModuleCount();
		const parts: string[] = [];
		for (let row = 0; row < modules; row++) {
			for (let col = 0; col < modules; col++) {
				if (qr.isDark(row, col)) parts.push(`M${col} ${row}h1v1h-1z`);
			}
		}
		return { path: parts.join(""), count: modules };
	}, [value]);

	return (
		<svg
			viewBox={`-1 -1 ${count + 2} ${count + 2}`}
			width={size}
			height={size}
			role="img"
			aria-label={label ?? `QR code for ${value}`}
			className="rounded-2xl bg-white p-2 shadow-lg"
			shapeRendering="crispEdges"
		>
			<title>{label ?? `QR code for ${value}`}</title>
			<rect x={-1} y={-1} width={count + 2} height={count + 2} fill="#fff" />
			<path d={path} fill="#1e1b4b" />
		</svg>
	);
}
